import React, {
  FocusEvent,
  ReactNode,
  useMemo,
  RefObject,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import MuiSelect, {
  SelectClasses as MuiSelectClasses,
  SelectChangeEvent,
} from '@mui/material/Select';
import {
  MenuProps,
} from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Label from 'pages/Dashboard/components/Label';
import {
  ChevronDownIcon,
} from 'core/icons';
import {
  SxProps,
  Theme,
} from '@mui/material/styles';

const NO_OPTIONS_VALUE = '__populate_select_no_options__';

type GetValue<T> = (option: T, index?: number) => string | number;
type GetLabel<T> = (option: T, index?: number) => string | ReactNode;
type GetDisableStatus<T> = (option: T, index?: number) => boolean;

export type Props<T> = {
  id?: string;
  className?: string;
  labelClasses?: string;
  selectClassName?: string;
  name?: string;
  label?: string;
  required?: boolean;
  multiple?: boolean;
  placeholder?: string;
  defaultValue?: string;
  value?: T | T[] | null;
  options: T[];
  getValue: GetValue<T>;
  getLabel: GetLabel<T>;
  getDisableStatus?: GetDisableStatus<T>;
  onChange: (option: T | T[]) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  renderValue?: (value: string) => ReactNode;
  disabled?: boolean;
  muiClasses?: Partial<MuiSelectClasses>;
  error?: boolean;
  showErrorText?: boolean;
  allowInvalid?: boolean;
  inputRef?: RefObject<any>;
  sx?: SxProps<Theme>;
  menuProps?: Partial<MenuProps>;
  menuItemClassName?: string;
  noOptionsText?: ReactNode;
  children?: ReactNode | ((
    options: T[],
    getValue: GetValue<T>,
    getLabel: GetLabel<T>,
    getDisableStatus?: GetDisableStatus<T>,
  ) => ReactNode);
};

export const SELECT_MENU_OFFSET_PX = 4;

function composePaperSx(paperSx?: SxProps<Theme>): SxProps<Theme> {
  const menuOffsetSx = { mt: `${SELECT_MENU_OFFSET_PX}px` };

  return isNil(paperSx)
    ? menuOffsetSx
    : [
      menuOffsetSx,
      ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
    ];
}

export function getSelectMenuProps(
  menuProps?: Partial<MenuProps>,
): Partial<MenuProps> {
  const paperSlot = menuProps?.slotProps?.paper;
  const paperObject = typeof paperSlot === 'function' ? undefined : paperSlot;

  return {
    ...menuProps,
    slotProps: {
      ...menuProps?.slotProps,
      paper: {
        ...paperObject,
        sx: composePaperSx(paperObject?.sx),
      },
    },
  };
}

export default function Select<T>({
  id = '',
  className,
  name,
  labelClasses,
  selectClassName,
  label,
  required,
  placeholder,
  defaultValue,
  value,
  options = [],
  getValue,
  getLabel,
  onChange,
  onBlur,
  multiple,
  renderValue,
  disabled,
  muiClasses,
  error,
  inputRef,
  allowInvalid = false,
  showErrorText = true,
  getDisableStatus,
  children,
  sx,
  menuProps,
  menuItemClassName,
  noOptionsText = 'No Options',
}: Props<T>) {
  const classes = clsx(
    'flex flex-col space-y-0.5 relative',
    className,
    { 'has-error': error },
  );
  const selectClasses = clsx(
    'populate-select',
    selectClassName,
    { disabled },
  );
  const optionsMap = useMemo(() => new Map(
    options.map((item) => [String(getValue(item)), item] as const),
  ), [options, getValue]);

  // Have to do because of broken typings of Material Select component
  // It does not have typings in case of multiple selection
  const singleSelectValue = useMemo(() => {
    const _placeholder = placeholder ?? '';
    if (typeof value === 'undefined') return _placeholder;
    const _value = String(getValue(value as T));
    const isValidValue = allowInvalid || !isNil(optionsMap.get(_value));
    return isValidValue ? _value : _placeholder;
  }, [value, placeholder, optionsMap, allowInvalid]);

  const multiSelectValue = useMemo(() => {
    const _values = ((multiple ? value : []) as T[])
      .map((value: T) => String(getValue(value)))
      .filter((value: string) => allowInvalid || optionsMap.has(value));
    return placeholder && !_values.length ? [placeholder] : _values;
  }, [value, placeholder, optionsMap, allowInvalid]);

  const _value = useMemo(() => (
    multiple ? multiSelectValue : singleSelectValue
  ), [multiple, multiSelectValue, singleSelectValue]);

  const handleChange = (event: SelectChangeEvent) => {
    const { target: { value } } = event;
    if (Array.isArray(value)) {
      const values: T[] = value
        .filter((_value: string) => _value !== placeholder)
        .map((_value: string) => optionsMap.get(_value) as T)
        .filter((_value: T) => !!_value);
      return onChange(values);
    }
    const _value: T = optionsMap.get(String(value)) as T;
    if (!isNil(_value)) {
      onChange(_value);
    }
    return null;
  };
  const renderedChildren = typeof children === 'function'
    ? children(options, getValue, getLabel, getDisableStatus)
    : children;

  return (
    <div className={classes}>
      {label && (
        <Label
          className={labelClasses}
          id={id}
          label={label}
          required={required}
        />
      )}
      {error && showErrorText && (
        <span className="absolute right-0 !text-xs text-red-500 font-semibold -top-1">
          Invalid entry
        </span>
      )}
      <MuiSelect
        className={selectClasses}
        id={id}
        name={name}
        defaultValue={defaultValue}
        value={_value as string}
        label={label}
        IconComponent={ChevronDownIcon}
        classes={{
          ...muiClasses,
          select: clsx(muiClasses?.select, { '!pr-3': disabled }),
          icon: clsx(muiClasses?.icon, { '!hidden': disabled }),
        }}
        multiple={multiple}
        onChange={handleChange}
        renderValue={renderValue}
        inputRef={inputRef}
        disabled={disabled}
        onBlur={onBlur}
        sx={sx}
        MenuProps={getSelectMenuProps(menuProps)}
      >
        {placeholder && (
          <MenuItem
            className="!hidden"
            value={placeholder}
            disabled
          >
            {placeholder}
          </MenuItem>
        )}
        {options.length === 0 && isNil(children) && (
          <MenuItem
            disabled
            value={NO_OPTIONS_VALUE}
          >
            {noOptionsText}
          </MenuItem>
        )}
        {renderedChildren ?? options.map((option, index) => (
          <MenuItem
            key={getValue(option, index)}
            value={getValue(option, index)}
            disabled={getDisableStatus?.(option, index) ?? false}
            className={menuItemClassName}
          >
            {getLabel(option, index)}
          </MenuItem>
        ))}
      </MuiSelect>
    </div>
  );
}
